import type { ResolvedDependency } from '@wix/ambassador-app-service-webapp/http';
import { DependencyNodeType } from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';
import type {
  AppRuntimeData,
  DependencyNode,
  GetAppsRuntimeDataResponse,
  RuntimeAppDependency,
} from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';
import type { HttpClient } from '@wix/http-client';

const APPS_RUNTIME_DATA_URL = '/_api/apps-runtime-reader/v1/app-runtime-data';
const APP_DEPENDENCIES_FIELD = 'APP_DEPENDENCIES';
const SINGLE_DEPENDENCY = 'SINGLE_DEPENDENCY';
const RESOLVED_SINGLE_DEPENDENCY = 'RESOLVED_SINGLE_DEPENDENCY';
const DEPENDENCIES_LOGIC_TREE = 'DEPENDENCIES_LOGIC_TREE';
const OR_OPERATOR = 'OR';

const pruneSingleDependency = (node: DependencyNode): DependencyNode => node;

const pruneResolvedSingleDependency = (
  node: DependencyNode,
): DependencyNode => {
  const subDeps = node.resolvedSingleDependencyOptions?.dependencies;
  if (!subDeps) {
    return node;
  }
  return {
    ...node,
    resolvedSingleDependencyOptions: {
      ...node.resolvedSingleDependencyOptions,
      dependencies: pruneOrBranches(subDeps),
    },
  };
};

const pruneDependenciesLogicTree = (
  node: DependencyNode,
): DependencyNode | undefined => {
  const tree = node.dependenciesLogicTreeOptions;
  if (tree?.operator === OR_OPERATOR) {
    return undefined;
  }
  const prunedChildren = (tree?.dependencies ?? [])
    .map(pruneOrBranches)
    .filter((child): child is DependencyNode => child !== undefined);
  if (prunedChildren.length === 0) {
    return undefined;
  }
  return {
    ...node,
    dependenciesLogicTreeOptions: { ...tree, dependencies: prunedChildren },
  };
};

const pruneOrBranches = (node: DependencyNode): DependencyNode | undefined => {
  if (node.nodeType === SINGLE_DEPENDENCY) {
    return pruneSingleDependency(node);
  } else if (node.nodeType === RESOLVED_SINGLE_DEPENDENCY) {
    return pruneResolvedSingleDependency(node);
  } else if (node.nodeType === DEPENDENCIES_LOGIC_TREE) {
    return pruneDependenciesLogicTree(node);
  }
  return node;
};

const collectAppIds = (node: DependencyNode): string[] => {
  if (node.nodeType === DependencyNodeType.SINGLE_DEPENDENCY) {
    const appId = node.singleDependencyOptions?.appId;
    return appId ? [appId] : [];
  }
  if (node.nodeType === DependencyNodeType.RESOLVED_SINGLE_DEPENDENCY) {
    const appId = node.resolvedSingleDependencyOptions?.appId;
    const subDeps = node.resolvedSingleDependencyOptions?.dependencies;
    return [
      ...(appId ? [appId] : []),
      ...(subDeps ? collectAppIds(subDeps) : []),
    ];
  }
  if (node.nodeType === DependencyNodeType.DEPENDENCIES_LOGIC_TREE) {
    return (node.dependenciesLogicTreeOptions?.dependencies ?? []).flatMap(
      collectAppIds,
    );
  }
  return [];
};

export const collectRuntimeDependencyAppIds = (
  runtimeDependency?: RuntimeAppDependency,
): string[] => {
  const root = runtimeDependency?.dependencies;
  if (!root) {
    return [];
  }
  const pruned = pruneOrBranches(root);
  return pruned ? collectAppIds(pruned) : [];
};

const collectSingleDependency = (
  node: DependencyNode,
  parentAppId: string,
): ResolvedDependency[] => {
  const appId = node.singleDependencyOptions?.appId;
  return appId
    ? [{ appId, parentRelations: [{ appId: parentAppId, dependencyData: [] }] }]
    : [];
};

const collectResolvedSingleDependency = (
  node: DependencyNode,
  parentAppId: string,
): ResolvedDependency[] => {
  const opts = node.resolvedSingleDependencyOptions;
  const appId = opts?.appId;
  if (!appId) {
    return [];
  }
  const subDeps = opts?.dependencies
    ? collectDependencies(opts.dependencies, appId)
    : [];
  return [
    { appId, parentRelations: [{ appId: parentAppId, dependencyData: [] }] },
    ...subDeps,
  ];
};

const collectDependenciesLogicTree = (
  node: DependencyNode,
  parentAppId: string,
): ResolvedDependency[] =>
  (node.dependenciesLogicTreeOptions?.dependencies ?? []).flatMap((child) =>
    collectDependencies(child, parentAppId),
  );

const collectDependencies = (
  node: DependencyNode,
  parentAppId: string,
): ResolvedDependency[] => {
  if (node.nodeType === SINGLE_DEPENDENCY) {
    return collectSingleDependency(node, parentAppId);
  } else if (node.nodeType === RESOLVED_SINGLE_DEPENDENCY) {
    return collectResolvedSingleDependency(node, parentAppId);
  } else if (node.nodeType === DEPENDENCIES_LOGIC_TREE) {
    return collectDependenciesLogicTree(node, parentAppId);
  }
  return [];
};

const convertToResolvedDependencies = (
  parentAppId: string,
  appRuntimeData: AppRuntimeData,
): ResolvedDependency[] => {
  const dependenciesNode = appRuntimeData.dependencies?.dependencies;
  if (!dependenciesNode) {
    return [];
  }
  const pruned = pruneOrBranches(dependenciesNode);
  if (!pruned) {
    return [];
  }
  return collectDependencies(pruned, parentAppId);
};

const getAppDependencies = async (
  httpClient: HttpClient,
  appId: string,
): Promise<ResolvedDependency[]> => {
  const { data } = await httpClient.request(() => ({
    method: 'POST',
    url: APPS_RUNTIME_DATA_URL,
    data: {
      appRuntimeKeys: [{ appId }],
      fields: [APP_DEPENDENCIES_FIELD],
      doNotIncludeComponents: true,
    },
  }));

  const appRuntimeData = (
    data as GetAppsRuntimeDataResponse
  )?.appsRuntimeData?.find((app) => app.appId === appId);
  if (!appRuntimeData) {
    return [];
  }

  return convertToResolvedDependencies(appId, appRuntimeData);
};

export { getAppDependencies };
